import React, { useEffect, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import UserAutocomplete from '../../generic/qr/labelPrinting/UserAutocomplete'
import { getDefaultPrinter, listAssignedUsers, outcomeOf, setDefaultPrinter } from '../../generic/qr/labelPrinting/labelPrintingApi'
import { OUTCOME_TEXT, textOf } from '../../generic/qr/labelPrinting/labelPrintingTexts'

// E13 answers of PUT labelPrinters/default for another user.
const ASSIGN_ERROR_TEXT = {
  FORBIDDEN: 'Only administrators can assign printers',
  USER_NOT_FOUND: 'The user does not exist',
  PRINTER_INACTIVE: 'The printer is inactive',
  PRINTER_NOT_FOUND: 'The printer no longer exists'
}

// Historia 226, E13 + E21 + E23 (S213, S230, S233): an ADMIN or SUPERADMIN decides to which users this printer is the default.
// The dialog opens with the users it is already assigned to chosen (`GET labelPrinters/{id}/assignedUsers`); next to each
// user it shows the printer they have now (`GET labelPrinters/default?username=`): "now: <other printer>" warns that adding
// them moves them off it. Saving makes one PUT labelPrinters/default per CHANGE: a removed user → `printerId: null`, an added
// one → this printer; it summarises and keeps pending only what failed. The list reloads when the dialog closes.
const AssignDefaultPrinterDialog = ({ printer, printers, onClose, onDone }) => {
  const { t_ } = useSmartTranslation()
  const [users, setUsers] = useState([])
  const [assigned, setAssigned] = useState([]) // what is saved in Sun now
  const [loaded, setLoaded] = useState(false)
  const [readFailed, setReadFailed] = useState(false) // the assigned users could not be read: what is saved is unknown
  const [current, setCurrent] = useState({}) // username → printer id | '' (none) | 'error' | 'loading'
  const [saving, setSaving] = useState(false)
  const [summary, setSummary] = useState({ text: '', failures: [] })

  // The users it is assigned to open as chosen; their current printer is this one, no need to ask.
  useEffect(() => {
    let alive = true
    listAssignedUsers(printer.id)
      .then((response) => {
        const usernames = Array.isArray(response?.usernames) ? response.usernames : []
        if (!alive) return
        setAssigned(usernames)
        setUsers(usernames)
        setCurrent((known) => usernames.reduce((all, username) => ({ ...all, [username]: printer.id }), known))
      })
      .catch((error) => {
        // 403 FORBIDDEN (not an administrator) or 404 PRINTER_NOT_FOUND (deleted meanwhile): say it instead of opening empty.
        if (!alive) return
        setReadFailed(true)
        setSummary({ text: t_(textOf(ASSIGN_ERROR_TEXT, outcomeOf(error)) || 'Unexpected error'), failures: [] })
      })
      .finally(() => { if (alive) setLoaded(true) })
    return () => { alive = false }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printer.id])

  // The current printer of every chosen user not read yet.
  useEffect(() => {
    users.filter((username) => !(username in current)).forEach(async (username) => {
      setCurrent((known) => ({ ...known, [username]: 'loading' }))
      let value = ''
      try {
        value = (await getDefaultPrinter(username))?.printerId || ''
      } catch (error) {
        value = 'error'
      }
      setCurrent((known) => ({ ...known, [username]: value }))
    })
  }, [users, current])

  const printerNameOf = (id) => (printers.find((candidate) => candidate.id === id) || {}).name || `#${id}`
  const currentText = (username) => {
    const value = current[username]
    if (value === 'loading' || value === undefined) return '…'
    if (value === 'error') return '—'
    if (value === '') return t_('No printer')
    return value === printer.id ? printerNameOf(value) : t_('now: {{printer}}', { printer: printerNameOf(value) })
  }

  const removed = assigned.filter((username) => !users.includes(username))
  const added = users.filter((username) => !assigned.includes(username))
  const changes = removed.length + added.length

  const handleUsersChange = (chosen) => setUsers(chosen)

  const confirm = async () => {
    if (saving || readFailed || changes === 0) return
    setSaving(true)
    const failures = []
    const saved = []
    for (const username of [...removed, ...added]) {
      try {
        await setDefaultPrinter(removed.includes(username) ? null : printer.id, username)
        saved.push(username)
      } catch (error) {
        failures.push({ username, text: t_(textOf(ASSIGN_ERROR_TEXT, outcomeOf(error)) || textOf(OUTCOME_TEXT, outcomeOf(error)) || 'Unexpected error') })
      }
    }
    setSaving(false)
    // What was saved is now the baseline; only the failures stay pending.
    setAssigned((before) => [...before.filter((username) => !saved.includes(username) || added.includes(username)), ...saved.filter((username) => added.includes(username) && !before.includes(username))])
    setCurrent((known) => saved.reduce((all, username) => ({ ...all, [username]: added.includes(username) ? printer.id : '' }), known))
    const total = removed.length + added.length
    if (failures.length === 0) {
      onDone(removed.length > 0
        ? t_('{{count}} changes saved', { count: total })
        : t_('{{done}} of {{total}} assigned', { done: total, total }))
      return
    }
    setSummary({
      text: removed.length > 0
        ? t_('{{done}} of {{total}} changes saved', { done: saved.length, total })
        : t_('{{done}} of {{total}} assigned', { done: saved.length, total }),
      failures
    })
  }

  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth='xs'>
      <DialogTitle>{t_('Assign to users')}</DialogTitle>
      <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant='body2' color='text.secondary'>{printer.name}</Typography>
        <UserAutocomplete multiple value={users} onChange={handleUsersChange} label={t_('Users')} />
        {users.map((username) => (
          <Box key={username} data-testid={`assign-current-${username}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, fontSize: 14 }}>
            <span>{username}</span>
            <Typography component='span' variant='body2' color='text.secondary'>{currentText(username)}</Typography>
          </Box>
        ))}
        {summary.text && (
          <Alert severity='error'>
            <div>{summary.text}</div>
            {summary.failures.map(({ username, text }) => <div key={username}>{`${username}: ${text}`}</div>)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>{t_('Cancel')}</Button>
        <Button
          variant='contained'
          onClick={confirm}
          disabled={!loaded || readFailed || changes === 0 || saving}
          startIcon={saving ? <CircularProgress size={16} color='inherit' /> : undefined}
        >
          {t_('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AssignDefaultPrinterDialog
